import React from 'react';
import './OngoingChargesReceipt.css';
import { dateTimeFormatter, dateTimeFormatter2 } from '../Utils/DateTimeFormatter';

// Helper to group charges and calculate subtotals
const groupChargesWithSubtotal = (charges) => {
    const grouped = {};
    charges.forEach(charge => {
        const type = charge.chargeType || 'Others';
        if (!grouped[type]) grouped[type] = { items: [], subtotal: 0 };
        grouped[type].items.push(charge);
        grouped[type].subtotal += parseFloat(charge.amount) || 0;
    });

    // Ensure Room Charges first, then others alphabetically
    const ordered = {};
    if (grouped["Room Charges"]) {
        ordered["Room Charges"] = grouped["Room Charges"];
        delete grouped["Room Charges"];
    }

    Object.keys(grouped)
        .sort()
        .forEach(type => {
            ordered[type] = grouped[type];
        });

    return ordered;
};

const OngoingChargesReceipt = React.forwardRef(({ patient, charges, invoiceSummary, prescriptionInvoices, ipInvoices }, ref) => {
    const groupedCharges = groupChargesWithSubtotal(charges || []);
    console.log("prerscription invoices:", prescriptionInvoices);
    console.log("IP invoices payments:", ipInvoices);

    return (
        <div className="print-area">
            <div className="receipt-container" ref={ref}>
                {/* Header */}
                <div className="receipt-header">
                    <img src="/VThospitalsLogo.png" alt="Logo" className="logo" />
                    <h2 className="clinic-name">VISWATEJA HOSPITAL & DIAGNOSTIC CENTRE</h2>
                    <p className="clinic-address">26-4-788/1, RPGT Road, Near Srinivasa Talkies, Hindupur - 515201</p>
                </div>

                {/* Patient Info */}
                <table className="info-table">
                    <tbody>
                        <tr>
                            <td><strong>Patient Name:</strong></td>
                            <td>{patient.patientName}</td>
                            <td><strong>MR No:</strong></td>
                            <td>{patient.mrNumber}</td>
                        </tr>
                        <tr>
                            <td><strong>Age/Gender:</strong></td>
                            <td>{patient.age} / {patient.gender}</td>
                            <td><strong>Phone:</strong></td>
                            <td>{patient.phoneNumber}</td>
                        </tr>
                        <tr>
                            <td><strong>Room/Bed:</strong></td>
                            <td>{patient.roomType} / {patient.bedNumber}</td>
                            <td><strong>Doctor:</strong></td>
                            <td>{patient.doctorName}</td>
                        </tr>
                        <tr>
                            <td><strong>Admission Time:</strong></td>
                            <td>{dateTimeFormatter(patient.admissionDate, patient.admissionTime)}</td>
                            <td><strong>Discharge Time:</strong></td>
                            <td>{dateTimeFormatter(patient.dischargeDate, patient.dischargeTime)}</td>
                        </tr>
                    </tbody>
                </table>
                {/* Invoice Details */}
                <h3 className="section-heading">Invoice Details</h3>
                <table className="items-table">
                    <thead>
                        <tr>
                            <th>Invoice ID</th>
                            <th>Invoice Date/Time</th>
                            <th>Invoice Amount</th>
                            <th>Amount Paid</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {[...(prescriptionInvoices || []), ...(ipInvoices || [])].map((invoice) => (
                            <tr key={invoice.invoiceId}>
                                <td>{invoice.invoiceId}</td>
                                <td>{dateTimeFormatter2(invoice.invoiceTime)}</td>
                                <td>₹{invoice.totalAmount}</td>
                                <td>₹{invoice.amountPaid}</td>
                                <td>{invoice.invoiceStatus}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Payment Details */}
                <h3 className="section-heading">Payment Details</h3>
                <table className="items-table">
                    <thead>
                        <tr>
                            <th>Payment ID</th>
                            <th>Invoice ID</th>
                            <th>Date/Time</th>
                            <th>Transaction Type</th>
                            <th>Mode</th>
                            <th>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {[...(prescriptionInvoices || []), ...(ipInvoices || [])]
                            .flatMap((invoice) =>
                                (invoice.payments || []).map((pmt) => (
                                    <tr key={pmt.paymentId}>
                                        <td>{pmt.paymentId}</td>
                                        <td>{invoice.invoiceId}</td>
                                        <td>{dateTimeFormatter2(pmt.paymentDate)}</td>
                                        <td>{pmt.paymentMode || '-'}</td>
                                        <td>{pmt.paymentMode}</td>
                                        <td>₹{pmt.amount}</td>
                                    </tr>
                                ))
                            )}
                    </tbody>
                </table>



                {/* Charges by Group */}
                {Object.entries(groupedCharges).map(([type, data], i) => (
                    <div key={i}>
                        <h3 className="section-heading">{type}</h3>
                        <table className="items-table">
                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>Particulars</th>
                                    <th>Date</th>
                                    <th>Usage</th>
                                    <th>Unit Cost</th>
                                    <th>Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((charge, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{charge.chargeName}</td>
                                        <td>{dateTimeFormatter2(charge.chargeDate)}</td>
                                        <td>{charge.usage || '-'}</td>
                                        <td>₹ {charge.unitCost || '-'}</td>
                                        <td>₹ {charge.amount}</td>
                                    </tr>
                                ))}
                                <tr className="subtotal-row">
                                    <td colSpan="5"><strong>Subtotal ({type})</strong></td>
                                    <td><strong>₹ {data.subtotal.toFixed(2)}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                ))}

                {/* Pharmacy Medicines Section */}
                {prescriptionInvoices && prescriptionInvoices.length > 0 && (
                    <div>
                        <h3 className="section-heading">Pharmacy</h3>
                        <table className="items-table">
                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>Item</th>
                                    <th>Quantity</th>
                                    <th>MRP</th>
                                    <th>Discount</th>
                                    <th>Price</th>
                                </tr>
                            </thead>
                            <tbody>
                                {prescriptionInvoices.flatMap((invoice, invIndex) =>
                                    (invoice.prescribedMedicines || []).map((med, index) => (
                                        <tr key={`${invIndex}-${index}`}>
                                            <td>{index + 1}</td>
                                            <td>{(med.medicineBrand || '') + (med.composition ? ` (${med.composition})` : '')}</td>
                                            <td>{med.quantity}</td>
                                            <td>₹ {med.mrp || '-'}</td>
                                            <td>{med.discount || 0}%</td>
                                            <td>₹ {med.price}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}


                {/* Grand Total Summary */}
                <div class="bottom-section">
                    <div class="right-content">
                        {invoiceSummary && (
                            <div className="receipt-grand-total-table">
                                <table>
                                    <tbody>
                                        <tr>
                                            <td>Grand total :</td>
                                            <td>₹ {invoiceSummary.grandTotal}</td>
                                        </tr>
                                        <tr>
                                            <td>Total charge :</td>
                                            <td>₹ {invoiceSummary.totalCharge}</td>
                                        </tr>
                                        <tr>
                                            <td>IP paid amount :</td>
                                            <td>₹ {invoiceSummary.ipPaidAmount}</td>
                                        </tr>
                                        <tr>
                                            <td>Pharmacy Paid amount :</td>
                                            <td>₹ {invoiceSummary.pharmacyPaidAmount}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Balance:</strong></td>
                                            <td>₹ {invoiceSummary.totalBalance}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* ✅ Clear floats so footer is placed below */}
                    <div className="clearfix"></div>

                    {/* Footer */}
                    <div className="footer-info">
                        <span><strong>Generated By:</strong> <br />{"System"}</span>
                        <span><strong><br />Authorized Signature</strong></span>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default OngoingChargesReceipt;
